/**
 * Turns a parked SafeGuard draft into the one sentence (plus the concrete
 * points) the reading pane should show. The new inbox used to render the
 * mail and nothing else, so a held contact looked like a normal letter.
 *
 * The stored fields differ by which gate stopped the send. This picks the
 * most specific story and prefers the text SafeGuard actually wrote
 * (explanation, issues, validation reason) over a generic label.
 */

const PARKED_REASONS = new Set([
  'safeguard_rejection',
  'scorer_rejection',
  'generation_error',
  'crm_blocked',
  'reply_received',
  'email_invalid_final',
]);

const SCORE_TAGS = {
  spam_words: 'Spam-Wörter erkannt',
  too_many_links: 'Zu viele Links',
  hyperbole: 'Übertriebene Sprache',
  no_clear_value: 'Kein klarer Mehrwert',
  too_short: 'Text zu kurz',
  too_long: 'Text zu lang',
  missing_personalization: 'Keine Personalisierung',
  aggressive_cta: 'Aggressiver Call-to-Action',
  all_caps: 'Großbuchstaben-Spam',
  excessive_punctuation: 'Zu viele Satzzeichen',
};

const GENERATION = {
  insufficient_credits: {
    title: 'Nicht genügend Credits',
    pill: 'Credits',
  },
  voice_clone_no_clone: {
    title: 'Keine Stimme ausgewählt',
    pill: 'Voice Clone',
  },
  generation_failed: {
    title: 'E-Mail konnte nicht geschrieben werden',
    pill: 'Generierung',
  },
  empty_body: {
    title: 'Leere E-Mail',
    pill: 'Generierung',
  },
  subject_instruction_no_apikey: {
    title: 'Betreff konnte nicht erzeugt werden',
    pill: 'Betreff',
  },
  subject_instruction_failed: {
    title: 'Betreff konnte nicht erzeugt werden',
    pill: 'Betreff',
  },
  subject_instruction_incomplete: {
    title: 'Betreff unvollständig',
    pill: 'Betreff',
  },
  subject_instruction_error: {
    title: 'Betreff konnte nicht erzeugt werden',
    pill: 'Betreff',
  },
  password_decrypt_failed: {
    title: 'Absender-Zugang ungültig',
    pill: 'Zugang',
  },
  transport_error: {
    title: 'Versandweg ausgefallen',
    pill: 'Versand',
  },
  smtp_error: {
    title: 'SMTP-Versand fehlgeschlagen',
    pill: 'SMTP',
  },
};

const EMPTY_REASON = 'SafeGuard hat diese Mail angehalten. Ein genauer Grund wurde nicht mitgespeichert.';
const BODY_LIMIT = 700;

const clean = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value !== 'string') return '';
  return value.replace(/\s+/g, ' ').trim();
};

const listOf = (value) => (
  Array.isArray(value) ? value.map(clean).filter(Boolean) : []
);

const clip = (text) => {
  const t = clean(text);
  if (t.length <= BODY_LIMIT) return t;
  return `${t.slice(0, BODY_LIMIT - 1).trim()}…`;
};

const isRatingSummary = (text) => /^safeguard rating \d+\s*\/\s*\d+/i.test(clean(text));

const stripAutoSendPrefix = (info) => {
  const text = clean(info);
  const match = text.match(/^Auto-Send Fehler:\s*.+?\s+[—–-]\s+([\s\S]+)$/i);
  return match ? clean(match[1]) : text;
};

const uniquePoints = (body, points) => {
  const hay = clean(body).toLowerCase();
  const seen = new Set();
  const out = [];
  points.forEach((raw) => {
    const point = clean(raw);
    const key = point.toLowerCase();
    if (!point || seen.has(key)) return;
    if (/^fehlergrund:/i.test(point) || /^details:/i.test(point)) return;
    if (hay && (hay === key || hay.includes(key))) return;
    seen.add(key);
    out.push(point);
  });
  return out.slice(0, 8);
};

const isServiceError = (email) => {
  if (email.safeGuardServiceError === true) return true;
  const info = clean(email.safeGuardInfo).toLowerCase();
  if (info.includes('safeguard call failed') || info.includes('safeguard evaluation failed')) return true;
  return listOf(email.safeGuardIssues).some((issue) => /safeguard (service|evaluation) error/i.test(issue));
};

const isDuplicateIntro = (email) => {
  if (email.duplicateIntroBlock && typeof email.duplicateIntroBlock === 'object') return true;
  const tags = email.tags;
  if (tags && !Array.isArray(tags) && tags.duplicate_intro) return true;
  const info = clean(email.safeGuardInfo).toLowerCase();
  return info.includes('introduction email was already sent') || info.includes('duplicate introduction');
};

const ratingPill = (email) => {
  const rating = Number(email.safeGuardRating);
  const minimum = Number(email.minimumRating);
  if (!Number.isFinite(rating) || rating <= 0) return null;
  if (Number.isFinite(minimum) && minimum > 0) return `${rating}/10 · Minimum ${minimum}`;
  return `${rating}/10`;
};

const providerLabel = (email) => {
  const provider = clean(email.crmBlock && email.crmBlock.provider).toLowerCase();
  if (provider === 'close') return 'Close';
  if (provider) return provider.charAt(0).toUpperCase() + provider.slice(1);
  return 'CRM';
};

const ratingSummaryTail = (info) => {
  const match = clean(info).match(/^SafeGuard Rating \d+\s*\/\s*\d+\s*[—–-]\s*(.+)$/i);
  return match ? clean(match[1]) : '';
};

// A joined issue list ("zu werblich, kein Bezug") becomes bullets. One longer
// sentence stays intact — splitting it on commas would shred the actual reason.
const splitRatingSummary = (info) => {
  const tail = ratingSummaryTail(info);
  if (!tail || /^score zu niedrig$/i.test(tail)) return [];
  const parts = tail.split(/,\s+/).map(clean).filter(Boolean);
  if (parts.length <= 1) return [];
  if (parts.some((part) => part.length > 80)) return [];
  return parts;
};

const qualityCopy = (email) => {
  const explanation = clean(email.safeGuardExplanation);
  const info = clean(email.safeGuardInfo);
  const rating = Number(email.safeGuardRating);
  const minimum = Number(email.minimumRating);
  let body = '';
  let points = listOf(email.safeGuardIssues);

  if (explanation) {
    body = explanation;
  } else if (info && !isRatingSummary(info)) {
    body = stripAutoSendPrefix(info);
  } else if (isRatingSummary(info)) {
    const tail = ratingSummaryTail(info);
    const parts = splitRatingSummary(info);
    if (points.length === 0 && parts.length > 1) points = parts;
    else if (points.length === 0 && tail && !/^score zu niedrig$/i.test(tail)) body = tail;
  }

  points = uniquePoints(body, points);
  if (!body && points.length === 0) {
    body = Number.isFinite(rating) && rating > 0 && Number.isFinite(minimum) && minimum > 0
      ? `Der Score liegt bei ${rating} von 10, das Minimum ist ${minimum}. Einzelne Punkte hat SafeGuard dazu nicht genannt.`
      : (info && !isRatingSummary(info) ? info : EMPTY_REASON);
  }

  const belowMinimum = Number.isFinite(rating) && rating > 0
    && Number.isFinite(minimum) && minimum > 0
    && rating < minimum;

  return {
    kind: 'quality',
    title: belowMinimum ? 'Text unter dem Minimum' : 'Prüfung nicht bestanden',
    body: clip(body),
    points,
    pill: ratingPill(email),
  };
};

/**
 * @param {object|null|undefined} email
 * @returns {{kind: string, title: string, body: string, points: string[], pill: string|null}|null}
 */
export function buildSafeGuardReason(email) {
  if (!email || typeof email !== 'object') return null;
  const parked = email.isSafeGuardDraft === true || PARKED_REASONS.has(email.reason);
  if (!parked) return null;

  if (isServiceError(email)) {
    const technical = clean(email.safeGuardErrorMessage) || clean(email.safeGuardInfo);
    const timeout = /timeout|zeitüberschreitung|zeitueberschreitung/i.test(technical);
    return {
      kind: 'service',
      title: timeout ? 'Prüfung nicht rechtzeitig fertig' : 'SafeGuard nicht erreichbar',
      body: timeout
        ? 'Die Mail wurde nicht gesendet, weil die Prüfung nicht rechtzeitig durchlief. Das ist kein Problem im Text — sie liegt nur sicherheitshalber hier.'
        : 'Die Mail wurde nicht gesendet, weil SafeGuard sie nicht bewerten konnte. Das ist kein Problem im Text — sie liegt nur sicherheitshalber hier.',
      points: uniquePoints('', technical ? [technical] : []),
      pill: timeout ? 'Timeout' : 'Ausgefallen',
    };
  }

  if (isDuplicateIntro(email)) {
    const body = clean(email.safeGuardInfo)
      || 'An diese Adresse ging schon eine Vorstellung. Die Mail wurde gestoppt, damit sich niemand zweimal vorstellt.';
    return {
      kind: 'duplicate',
      title: 'Vorstellung schon verschickt',
      body: clip(body),
      points: uniquePoints(body, listOf(email.safeGuardIssues)),
      pill: 'Doppelter Kontakt',
    };
  }

  if (email.reason === 'crm_blocked') {
    const provider = providerLabel(email);
    const body = clean(email.safeGuardInfo)
      || `Der Versand wurde gestoppt, weil der Kontakt in ${provider} schon liegt.`;
    return {
      kind: 'crm',
      title: `Schon in ${provider} vorhanden`,
      body: clip(body),
      points: uniquePoints(body, listOf(email.safeGuardIssues)),
      pill: provider,
    };
  }

  if (email.reason === 'email_invalid_final') {
    const block = email.emailValidationBlock || {};
    const status = clean(block.validationStatus);
    const why = clean(block.validationReason);
    const body = clean(email.safeGuardInfo)
      || `Die Adresse ist dauerhaft ungültig${why ? `: ${why}` : ''}. Eine Ersatzadresse zum Umschalten gab es nicht.`;
    const detail = status && why ? `${status}: ${why}` : why;
    return {
      kind: 'invalid',
      title: 'Adresse endgültig ungültig',
      body: clip(body),
      points: uniquePoints(body, [detail, ...listOf(email.safeGuardIssues)]),
      pill: 'Ungültig',
    };
  }

  if (email.reason === 'reply_received') {
    const body = clean(email.safeGuardInfo)
      || 'Das Follow-up wurde gestoppt, weil inzwischen eine Antwort eingegangen ist.';
    return {
      kind: 'reply',
      title: 'Kontakt hat schon geantwortet',
      body: clip(body),
      points: uniquePoints(body, listOf(email.safeGuardIssues)),
      pill: 'Follow-up gestoppt',
    };
  }

  if (email.reason === 'scorer_rejection') {
    const score = email.preSendScore && typeof email.preSendScore === 'object' ? email.preSendScore : {};
    const tags = listOf(score.reasonTags).map((tag) => SCORE_TAGS[tag] || tag);
    const composite = Number(score.compositeScore);
    const body = clean(email.safeGuardInfo) || 'Der Check vor dem Versand hat die Mail angehalten.';
    return {
      kind: 'scorer',
      title: 'Vor dem Versand zu schwach',
      body: clip(body),
      points: uniquePoints(body, [...tags, ...listOf(email.safeGuardIssues)]),
      pill: Number.isFinite(composite) ? `${composite}/10` : (ratingPill(email) || 'Score'),
    };
  }

  if (email.reason === 'generation_error' && email.errorReason !== 'safeguard_rejected') {
    const known = GENERATION[email.errorReason] || {
      title: 'Versand nicht fertig geworden',
      pill: 'Gestoppt',
    };
    const raw = clean(email.safeGuardInfo);
    const body = (raw ? stripAutoSendPrefix(raw) : '')
      || 'Die Mail wurde geparkt, weil sie nicht fertig erzeugt oder verschickt werden konnte.';
    return {
      kind: 'generation',
      title: known.title,
      body: clip(body),
      points: uniquePoints(body, listOf(email.safeGuardIssues)),
      pill: known.pill,
    };
  }

  return qualityCopy(email);
}

export function isSafeGuardParked(email) {
  return buildSafeGuardReason(email) !== null;
}
